import React, { useState, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { importService, ImportResponse } from '../services/importService';
import { useTranslation } from '../hooks/useTranslation';

interface ImportBankAccountsModalProps {
  companyId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ImportBankAccountsModal: React.FC<ImportBankAccountsModalProps> = ({
  companyId,
  isOpen,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importFormat, setImportFormat] = useState<'csv' | 'excel'>('csv');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const downloadTemplateMutation = useMutation({
    mutationFn: (format: 'csv' | 'excel') => importService.downloadBankAccountsTemplate(companyId, format),
    onSuccess: (blob, format) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bank_accounts_template.${format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  });

  const importMutation = useMutation({
    mutationFn: (file: File) => importService.importBankAccounts(companyId, file),
    onSuccess: (data: ImportResponse) => {
      if (data.errorCount === 0) {
        onSuccess();
        onClose();
        setSelectedFile(null);
      }
    }
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Определяем формат файла
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (extension === 'xlsx' || extension === 'xls') {
        setImportFormat('excel');
      } else {
        setImportFormat('csv');
      }
    }
  };

  const handleImport = () => {
    if (selectedFile) {
      importMutation.mutate(selectedFile);
    }
  };

  const handleDownloadTemplate = (format: 'csv' | 'excel') => {
    downloadTemplateMutation.mutate(format);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {t('companies.import.bankAccounts.title') || 'Импорт банковских счетов'}
          </h3>

          {/* Кнопки скачивания шаблона */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('companies.import.downloadTemplate') || 'Скачать шаблон'}
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => handleDownloadTemplate('csv')}
                disabled={downloadTemplateMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                CSV
              </button>
              <button
                onClick={() => handleDownloadTemplate('excel')}
                disabled={downloadTemplateMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                Excel
              </button>
            </div>
          </div>

          {/* Выбор файла */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('companies.import.selectFile') || 'Выбрать файл'}
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelect}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {selectedFile && (
              <p className="mt-2 text-sm text-gray-600">
                {t('companies.import.selectedFile') || 'Выбран файл'}: {selectedFile.name}
              </p>
            )}
          </div>

          {/* Результаты импорта */}
          {importMutation.data && (
            <div className={`mb-4 p-3 rounded-md ${
              importMutation.data.errorCount === 0 
                ? 'bg-green-50 text-green-800' 
                : 'bg-yellow-50 text-yellow-800'
            }`}>
              <p className="font-medium">
                {t('companies.import.imported') || 'Импортировано'}: {importMutation.data.importedCount}
              </p>
              {importMutation.data.errorCount > 0 && (
                <div className="mt-2">
                  <p className="font-medium">
                    {t('companies.import.errors') || 'Ошибки'}: {importMutation.data.errorCount}
                  </p>
                  <ul className="list-disc list-inside mt-1 text-sm">
                    {importMutation.data.errors.slice(0, 5).map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Кнопки */}
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                onClose();
                setSelectedFile(null);
                importMutation.reset();
              }}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              {t('common.close') || 'Закрыть'}
            </button>
            <button
              onClick={handleImport}
              disabled={!selectedFile || importMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {importMutation.isPending 
                ? (t('companies.import.importing') || 'Импорт...') 
                : (t('companies.import.import') || 'Импортировать')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportBankAccountsModal;

